/**
 * Saves a file the browser holds in memory.
 *
 * Desktop browsers (and Android) save a clicked `<a download href=blob:…>`.
 * iOS and iPadOS WebKit don't save it reliably: Safari opens a PDF blob in a
 * tab at `blob:https://www.imai.studio/<uuid>`, so the member can't keep or
 * send the file, and a shared copy of that tab's address is a dead link
 * (`https://www.imai.studio/<uuid>`). On those devices the file goes to the
 * system share sheet instead (Save to Files, AirDrop, WhatsApp…).
 *
 * `navigator.share` needs a fresh tap. Downloads fetch first, so the tap can
 * expire before the file is ready. The file is then handed to the pending
 * save prompt, where one more tap opens the share sheet.
 *
 * On iPhone and iPad a held file never falls back to a blob anchor: the prompt
 * keeps it for another try, or offers the file's own URL when there is one.
 */

export type SaveBlobOutcome =
  'downloaded' | 'shared' | 'cancelled' | 'prompted';

/**
 * Largest file handed to the share sheet. The bytes are already in memory, and
 * the share extension copies them again; past this iOS Safari can be killed or
 * reject the share after the member waited for the fetch.
 */
export const SHARE_FILE_MAX_BYTES = 100 * 1024 * 1024;

export type SaveBlobOptions = {
  nav?: ShareNavigator;
  /** The file's own URL, offered on iPhone/iPad when sharing can't work. */
  sourceUrl?: string | null;
};

type ShareNavigator = {
  userAgent: string;
  maxTouchPoints?: number;
  canShare?: (data: { files: File[] }) => boolean;
  share?: (data: { files: File[] }) => Promise<void>;
};

/** iPhone, iPod and iPad, including iPadOS that reports a Mac user agent. */
export function isAppleTouchDevice(nav: {
  userAgent: string;
  maxTouchPoints?: number;
}): boolean {
  if (/iPhone|iPad|iPod/.test(nav.userAgent)) return true;
  return /Macintosh/.test(nav.userAgent) && (nav.maxTouchPoints ?? 0) > 1;
}

/** Splits `report.pdf` into `report` and `.pdf` so a truncated name keeps its type. */
export function splitFileExtension(name: string): {
  stem: string;
  extension: string;
} {
  const dot = name.lastIndexOf('.');
  if (dot <= 0 || dot === name.length - 1) return { stem: name, extension: '' };
  return { stem: name.slice(0, dot), extension: name.slice(dot) };
}

export function toShareableFile(blob: Blob, filename: string): File {
  if (blob instanceof File && blob.name === filename) return blob;
  return new File([blob], filename, {
    type: blob.type || 'application/octet-stream',
  });
}

/** Whether this device's share sheet says it can take this file. */
export function canShareFile(nav: ShareNavigator, file: File): boolean {
  if (typeof nav.share !== 'function' || typeof nav.canShare !== 'function')
    return false;
  try {
    return nav.canShare({ files: [file] });
  } catch {
    return false;
  }
}

function errorName(error: unknown): string | null {
  return typeof error === 'object' && error !== null && 'name' in error
    ? String((error as { name: unknown }).name)
    : null;
}

/**
 * A file the prompt holds on iPhone/iPad.
 * - `ready`: the tap expired; one more tap opens the share sheet.
 * - `failed`: the share sheet refused it; the member can try again.
 * - `too_large`: over {@link SHARE_FILE_MAX_BYTES}.
 * - `unsupported`: this browser's share sheet can't take the file.
 * `too_large` and `unsupported` keep no bytes (`file` is null): they offer the
 * file's own URL, or (too large, no URL) say why it wasn't saved.
 */
export type PendingSave = {
  name: string;
  file: File | null;
  sourceUrl: string | null;
  state: 'ready' | 'failed' | 'too_large' | 'unsupported';
};

type PendingSaveListener = (pending: PendingSave | null) => void;
let pendingSave: PendingSave | null = null;
const listeners = new Set<PendingSaveListener>();

function setPendingSave(next: PendingSave | null) {
  pendingSave = next;
  for (const listener of listeners) listener(next);
}

export function getPendingSave(): PendingSave | null {
  return pendingSave;
}

export function subscribePendingSave(
  listener: PendingSaveListener
): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function dismissPendingSave() {
  setPendingSave(null);
}

function hold(
  state: PendingSave['state'],
  name: string,
  file: File | null,
  sourceUrl: string | null
): SaveBlobOutcome {
  setPendingSave({ name, file, sourceUrl, state });
  return 'prompted';
}

/** Called from the prompt's tap, so the share sheet has a fresh activation. */
export async function sharePendingSave(
  nav: ShareNavigator = navigator
): Promise<SaveBlobOutcome> {
  const pending = pendingSave;
  if (!pending?.file || typeof nav.share !== 'function') return 'cancelled';
  try {
    await nav.share({ files: [pending.file] });
    setPendingSave(null);
    return 'shared';
  } catch (error) {
    if (errorName(error) === 'AbortError') return 'cancelled';
    // Keep the only copy for another try; never a blob tab on iPhone/iPad.
    setPendingSave({ ...pending, state: 'failed' });
    return 'prompted';
  }
}

export function triggerAnchorDownload(blob: Blob, filename: string) {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  link.rel = 'noopener';
  link.style.display = 'none';
  document.body.appendChild(link);
  try {
    link.click();
  } finally {
    link.remove();
    // Give the browser time to start reading the object before it goes away.
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 30_000);
  }
}

/** Saves a `data:` URL (canvas and image exports) the same way as a Blob. */
export async function saveDataUrl(
  dataUrl: string,
  filename: string,
  options: SaveBlobOptions = {}
): Promise<SaveBlobOutcome> {
  const blob = await (await fetch(dataUrl)).blob();
  return saveBlob(blob, filename, options);
}

export async function saveBlob(
  blob: Blob,
  filename: string,
  options: SaveBlobOptions = {}
): Promise<SaveBlobOutcome> {
  const nav = options.nav ?? navigator;
  const sourceUrl = options.sourceUrl || null;
  if (!isAppleTouchDevice(nav)) {
    triggerAnchorDownload(blob, filename);
    return 'downloaded';
  }
  // An oversized file is not shared, and not kept in memory while the prompt shows.
  if (blob.size > SHARE_FILE_MAX_BYTES)
    return hold('too_large', filename, null, sourceUrl);
  const file = toShareableFile(blob, filename);
  if (typeof nav.share !== 'function') {
    // No Web Share at all. Its own URL opens as a real page; without one the
    // plain download is all this browser offers.
    if (sourceUrl) return hold('unsupported', filename, null, sourceUrl);
    triggerAnchorDownload(file, filename);
    return 'downloaded';
  }
  // Web Share exists, so never a blob tab. Without `canShare` the share itself
  // decides; a refusal there lands in the held `failed` state below.
  if (typeof nav.canShare === 'function' && !canShareFile(nav, file))
    return hold('unsupported', filename, null, sourceUrl);
  try {
    await nav.share({ files: [file] });
    return 'shared';
  } catch (error) {
    const name = errorName(error);
    if (name === 'AbortError') return 'cancelled';
    const state = name === 'NotAllowedError' ? 'ready' : 'failed';
    return hold(state, filename, file, sourceUrl);
  }
}

/** What the prompt says and offers for a held file. */
export function pendingSavePresentation(pending: PendingSave): {
  status:
    | 'ready'
    | 'failed'
    | 'tooLarge'
    | 'useComputer'
    | 'openToSave'
    | 'cantShare';
  action: 'share' | 'retry' | 'open' | null;
} {
  switch (pending.state) {
    case 'ready':
      return { status: 'ready', action: 'share' };
    case 'failed':
      return { status: 'failed', action: 'retry' };
    case 'too_large':
      return pending.sourceUrl
        ? { status: 'tooLarge', action: 'open' }
        : { status: 'useComputer', action: null };
    case 'unsupported':
      return pending.sourceUrl
        ? { status: 'openToSave', action: 'open' }
        : { status: 'cantShare', action: null };
  }
}
